import { Nullable } from "../../types/utils";
import { User as User_API } from "../api-entities/User";

export type User = {
  type: UserType.Customer;
  details: UserDetails;
} | {
  type: UserType.Admin;
  details: UserDetails;
} | {
  type: UserType.CCP;
  details: UserDetails;
} | {
  type: UserType.Unknown;
};

export type UserDetails = {
  name: string;
  userName: string;
  email: string;
  phone?: string;
  ccp?: number;
  organisationId?: number;
  organisationName?: string;
  organisationAddress?: string;
  countryCode: string;
  ecmwfCanLicence: boolean;
  ecmwfCanLicenceArchive: boolean;
  freeze: {
    message: Nullable<string>;
    enabled: boolean;
  }
};

export type UserWithDetails = User & { details: UserDetails };

/**
 * Construct a new user from its APi representation.
 */
export const makeUser = (user: User_API): User => {
  const type = getUserType(user.type);

  return {
    type,
    details: {
      name: user.name,
      userName: user.user_name,
      email: user.email,
      phone: user.phone,
      type: getUserType(user.type),
      organisationId: user.organisation_id,
      organisationName: user.organisation_name,
      organisationAddress: user.registered_address,
      countryCode: user.country_code,
      ecmwfCanLicence: user.ecmwf_can_licence,
      ecmwfCanLicenceArchive: user.ecmwf_can_licence_archive,
      freeze: user.freeze
    }
  } as User;
};

export enum UserType {
  Admin = 1,
  Customer = 2,
  CCP = 3,
  Unknown = 4
}

export const getUserType = (type: string) => {
  switch (type.toLowerCase()) {
    case "public":
      return UserType.Customer;
    case "admin":
      return UserType.Admin;
    case "ccp":
      return UserType.CCP;
  }

  throw new Error("We couldn't identify what type of user you are");
};

export const userHasCcpRole = (user: User): user is UserWithDetails =>
  user.type === UserType.CCP || user.type === UserType.Admin;

export const userHasAdminRole = (user: User): user is UserWithDetails =>
  user.type === UserType.Admin;
